// ─── Header ──────────────────────────────────────────────────────────────────
const Header = {
  el: null,
  mobileMenu: null,
  burger: null,
  lastScrollY: 0,
  isCourse: false,
  isSingle: false,
  isMobile: false,
  // state flags — evita classList ops redundantes a 60fps
  _scrolled: false,
  _visible: false,

  init() {
    this.el = document.querySelector(".site-header");
    this.mobileMenu = document.querySelector(".site-mobile-menu");
    this.burger = document.querySelector(".site-header__burger");

    if (!this.el) {
      return;
    }

    this.isCourse = this.el.classList.contains("site-header--course");
    this.isSingle = this.el.classList.contains("site-header--single");
    this.isMobile = window.matchMedia("(max-width: 1024px)").matches;

    // atualiza isMobile só em resize, nunca em scroll
    const mq = window.matchMedia("(max-width: 1024px)");
    mq.addEventListener("change", (e) => {
      this.isMobile = e.matches;
    });

    this.bindMobileMenu();
    this.bindDesktopDropdowns();
  },

  onScroll(scrollY) {
    if (!this.el) return;
    const threshold = 30;
    const delta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;

    if (this.isSingle) {
      if (this.isMobile) {
        const shouldScroll = scrollY > threshold;
        if (shouldScroll !== this._scrolled) {
          this._scrolled = shouldScroll;
          this.el.classList.toggle("is-scrolled", shouldScroll);
        }
      } else {
        if (delta < -8 && scrollY > threshold && !this._visible) {
          this._visible = true;
          this.el.classList.add("is-visible", "is-scrolled");
        } else if (delta > 8 && this._visible) {
          this._visible = false;
          this.el.classList.remove("is-visible", "is-scrolled");
        }
      }
    } else if (this.isCourse) {
      if (delta < -8 && scrollY > threshold && !this._visible) {
        this._visible = true;
        this.el.classList.add("is-visible");
      } else if (delta > 8 && this._visible) {
        this._visible = false;
        this.el.classList.remove("is-visible");
      }
    } else {
      const shouldScroll = scrollY > threshold;
      if (shouldScroll !== this._scrolled) {
        this._scrolled = shouldScroll;
        this.el.classList.toggle("is-scrolled", shouldScroll);
      }
    }
  },

  // ── Desktop dropdowns ──────────────────────────────────────────────────────
  bindDesktopDropdowns() {
    const items = this.el.querySelectorAll(
      ".site-header__menu-item--has-submenu",
    );

    items.forEach((item) => {
      const trigger = item.querySelector(".site-header__menu-trigger");
      const dropdown = item.querySelector(".site-header__dropdown");

      if (!trigger || !dropdown) {
        return;
      }

      // CSS handles the hover state; we only manage aria-expanded here
      item.addEventListener("mouseenter", () => {
        trigger.setAttribute("aria-expanded", "true");
      });

      item.addEventListener("mouseleave", () => {
        trigger.setAttribute("aria-expanded", "false");
      });

      // keyboard accessibility
      trigger.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          const open = trigger.getAttribute("aria-expanded") === "true";
          trigger.setAttribute("aria-expanded", String(!open));
          dropdown.style.pointerEvents = !open ? "all" : "none";
        }

        if (e.key === "Escape") {
          trigger.setAttribute("aria-expanded", "false");
          trigger.focus();
        }
      });
    });
  },

  // ── Mobile menu ────────────────────────────────────────────────────────────
  bindMobileMenu() {
    const menu = this.mobileMenu;
    const close = menu?.querySelector(".site-mobile-menu__close");

    if (!menu || !this.burger) {
      return;
    }

    this.burger.addEventListener("click", () => this.openMenu());
    close?.addEventListener("click", () => this.closeMenu());

    // close on backdrop click (outside the inner area)
    menu.addEventListener("click", (e) => {
      if (e.target === menu) this.closeMenu();
    });

    // close on Escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && menu.classList.contains("is-open")) {
        this.closeMenu();
      }
    });

    // mobile accordion items
    menu
      .querySelectorAll(".site-mobile-menu__item--has-submenu")
      .forEach((item) => {
        const trigger = item.querySelector(".site-mobile-menu__trigger");
        trigger?.addEventListener("click", () => this.toggleAccordion(item));
      });

    // close on any link click (handles same-page anchor navigation)
    menu.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => this.closeMenu());
    });
  },

  openMenu() {
    const menu = this.mobileMenu;

    if (!menu) {
      return;
    }

    menu.classList.add("is-open");
    menu.setAttribute("aria-hidden", "false");
    this.burger.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
  },

  closeMenu() {
    const menu = this.mobileMenu;

    if (!menu) {
      return;
    }

    menu.classList.remove("is-open");
    menu.setAttribute("aria-hidden", "true");
    this.burger.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";

    // reset all open submenus
    menu
      .querySelectorAll(".site-mobile-menu__item--has-submenu.is-open")
      .forEach((item) => {
        item.classList.remove("is-open");
        item
          .querySelector(".site-mobile-menu__trigger")
          ?.setAttribute("aria-expanded", "false");
      });
  },

  toggleAccordion(item) {
    const isOpen = item.classList.contains("is-open");
    const trigger = item.querySelector(".site-mobile-menu__trigger");

    // close all siblings
    item
      .closest(".site-mobile-menu__list")
      ?.querySelectorAll(".site-mobile-menu__item--has-submenu")
      .forEach((i) => {
        i.classList.remove("is-open");
        i.querySelector(".site-mobile-menu__trigger")?.setAttribute(
          "aria-expanded",
          "false",
        );
      });

    if (!isOpen) {
      item.classList.add("is-open");
      trigger?.setAttribute("aria-expanded", "true");
    }
  },
};

// ─── Institutional Header ─────────────────────────────────────────────────────
const InstHeader = {
  el: null,
  mobileMenu: null,
  burger: null,

  init() {
    this.el = document.querySelector(".inst-header");
    this.mobileMenu = document.querySelector(".inst-mobile-menu");
    this.burger = document.querySelector(".inst-header__burger");

    if (!this.el) {
      return;
    }

    this.bindDesktopDropdowns();
    this.bindMobileMenu();
  },

  bindDesktopDropdowns() {
    this.el
      .querySelectorAll(".inst-header__menu-item--has-submenu")
      .forEach((item) => {
        const trigger = item.querySelector(".inst-header__menu-trigger");

        if (!trigger) {
          return;
        }

        item.addEventListener("mouseenter", () => {
          trigger.setAttribute("aria-expanded", "true");
        });

        item.addEventListener("mouseleave", () => {
          trigger.setAttribute("aria-expanded", "false");
        });

        trigger.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            const open = trigger.getAttribute("aria-expanded") === "true";
            trigger.setAttribute("aria-expanded", String(!open));
          }
          if (e.key === "Escape") {
            trigger.setAttribute("aria-expanded", "false");
            trigger.focus();
          }
        });
      });
  },

  bindMobileMenu() {
    const menu = this.mobileMenu;
    const close = menu?.querySelector(".inst-mobile-menu__close");

    if (!menu || !this.burger) {
      return;
    }

    this.burger.addEventListener("click", () => this.openMenu());
    close?.addEventListener("click", () => this.closeMenu());

    menu.addEventListener("click", (e) => {
      if (e.target === menu) this.closeMenu();
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && menu.classList.contains("is-open")) {
        this.closeMenu();
      }
    });

    menu
      .querySelectorAll(".inst-mobile-menu__item--has-submenu")
      .forEach((item) => {
        const trigger = item.querySelector(".inst-mobile-menu__trigger");
        trigger?.addEventListener("click", () => this.toggleAccordion(item));
      });

    menu.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => this.closeMenu());
    });
  },

  openMenu() {
    const menu = this.mobileMenu;

    if (!menu) {
      return;
    }

    menu.classList.add("is-open");
    menu.setAttribute("aria-hidden", "false");
    this.burger.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
  },

  closeMenu() {
    const menu = this.mobileMenu;

    if (!menu) {
      return;
    }

    menu.classList.remove("is-open");
    menu.setAttribute("aria-hidden", "true");
    this.burger.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";

    menu
      .querySelectorAll(".inst-mobile-menu__item--has-submenu.is-open")
      .forEach((item) => {
        item.classList.remove("is-open");
        item
          .querySelector(".inst-mobile-menu__trigger")
          ?.setAttribute("aria-expanded", "false");
      });
  },

  toggleAccordion(item) {
    const isOpen = item.classList.contains("is-open");
    const trigger = item.querySelector(".inst-mobile-menu__trigger");

    item
      .closest(".inst-mobile-menu__list")
      ?.querySelectorAll(".inst-mobile-menu__item--has-submenu")
      .forEach((i) => {
        i.classList.remove("is-open");
        i.querySelector(".inst-mobile-menu__trigger")?.setAttribute(
          "aria-expanded",
          "false",
        );
      });

    if (!isOpen) {
      item.classList.add("is-open");
      trigger?.setAttribute("aria-expanded", "true");
    }
  },
};

// ─── Institutional Footer ─────────────────────────────────────────────────────
const InstFooter = {
  mountain: null,
  wrap: null,
  content: null,

  init() {
    this.mountain = document.querySelector(".inst-footer__mountain");
    this.wrap = document.querySelector(".inst-footer__mountain-wrap");
    this.content = document.querySelector(".inst-footer__content");

    if (!this.mountain || !this.wrap) {
      return;
    }

    this.updateParallax();
    this.bindBackToTop();
  },

  updateParallax() {
    if (!this.wrap || !this.mountain) {
      return;
    }

    const rect = (this.content || this.wrap).getBoundingClientRect();
    const vh = window.innerHeight;
    const offset = vh * 0.1; // 10vh

    // progress=0 → content bottom just entered viewport (rect.bottom = vh)
    // progress=1 → content bottom is 10vh above viewport bottom (rect.bottom = vh - offset)
    const progress = Math.max(0, Math.min(1, (vh - rect.bottom) / offset));

    // margin-top: -76px (start) → 10px (end)
    this.mountain.style.marginTop = `${-76 + progress * 86}px`;
  },

  bindBackToTop() {
    document
      .querySelector(".inst-footer__back-to-top")
      ?.addEventListener("click", () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  },
};

// ─── Profissionais Slider ─────────────────────────────────────────────────────
const ProfissionaisSlider = {
  init() {
    document
      .querySelectorAll(".home-profissionais__slider")
      .forEach((slider) => {
        const grid = slider.querySelector(".home-profissionais__grid");
        const prev = slider.querySelector(".home-profissionais__nav-btn--prev");
        const next = slider.querySelector(".home-profissionais__nav-btn--next");

        if (!grid || !prev || !next) {
          return;
        }

        const cardWidth = () => {
          const card = grid.querySelector(".home-profissionais__card");
          return card ? card.offsetWidth + 10 : grid.offsetWidth;
        };

        const update = () => {
          const cards = grid.querySelectorAll(".home-profissionais__card");

          if (!cards.length) {
            return;
          }

          const cw = cards[0].offsetWidth + 10;
          const index = Math.round(grid.scrollLeft / cw);
          prev.disabled = index <= 0;
          next.disabled = index >= cards.length - 1;
        };

        update();
        grid.addEventListener("scroll", update, { passive: true });

        prev.addEventListener("click", () =>
          grid.scrollBy({ left: -cardWidth(), behavior: "smooth" }),
        );
        next.addEventListener("click", () =>
          grid.scrollBy({ left: cardWidth(), behavior: "smooth" }),
        );
      });
  },
};

// ─── Main ────────────────────────────────────────────────────────────────────
const Main = {
  init() {
    this.initScroll();
    Header.init();
    InstHeader.init();
    InstFooter.init();
    ProfissionaisSlider.init();
  },

  initScroll() {
    window.addEventListener(
      "scroll",
      () => {
        Header.onScroll(window.scrollY);
        InstFooter.updateParallax();
      },
      { passive: true },
    );
  },

  initKeenBanner() {},

  Helpers: {
    debounce(func, wait) {
      let timeout;
      return function (...args) {
        const ctx = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(ctx, args), wait);
      };
    },
  },
};

document.addEventListener("DOMContentLoaded", () => Main.init());
